// hooks/useForecastSimulations.ts
import { useCallback, useContext, useEffect, useState } from 'react';
import { getSimulationHistory } from '@/services/simulations';
import { notification } from 'antd';
import dayjs from 'dayjs';
import ForecastService from '@/domain/Forecast/ForecastService';
import DashboardContext from '@/contexts/DashboardContext';

interface Sensor {
  serialNumberTool: string;
  infrastructureId: string;
  measurementTargetId: string;
  sourceId: string;
}

interface ReadingItem {
  originalParameterId: string;
  originalSourceSerialNumber: string | null;
  serialNumberMeasurementTool: string;
}

interface Simulation {
  id: string;
  startedAt: string;
}

interface ForecastHookResult {
  simulationsList: Simulation[];
  selectedSimulation: string | undefined;
  fetchSimulations: () => void;
  setSelectedSimulation: (id: string | undefined) => void;
  pastForecastData: any[];
  setPastForecastData: (data: any[]) => void;
  setShowPastForecast: (show: boolean) => void;
  showPastForecast: boolean;
  isLoadingSimulations: boolean;
  hasSimulationError: boolean;
}

const forecastService = new ForecastService();

export function useForecastSimulations(
  readingsList: ReadingItem[],
  selectedSensors: Sensor[],
  selectedDateRange: [string, string],
  forecastHandler: (simulationId: string) => void,
  cleanAreaSeries: () => void,
): ForecastHookResult {
  const { value: context, setValue: setContext } = useContext(DashboardContext);
  const [simulationsList, setSimulationsList] = useState<Simulation[]>([]);
  const [selectedSimulation, setSelectedSimulation] = useState<string | undefined>(
    undefined,
  );
  const [pastForecastData, setPastForecastData] = useState<any[]>([]);
  const [showPastForecast, setShowPastForecast] = useState(false);
  const [isLoadingSimulations, setIsLoadingSimulations] = useState(false);
  const [hasSimulationError, setHasSimulationError] = useState(false);

  const fetchPastForecastData = async (simulationId: any = null) => {
    try {
      cleanAreaSeries();
      const seen = new Set<string>();
      const validReadings = readingsList.filter((item) => {
        if (!item) return false;

        const key = JSON.stringify([
          item.originalParameterId,
          item.originalSourceSerialNumber,
        ]);
        if (seen.has(key)) return false;
        seen.add(key);

        return (
          item.originalSourceSerialNumber == null &&
          selectedSensors.some(
            (sensor) =>
              sensor.serialNumberTool === item.serialNumberMeasurementTool,
          )
        );
      });

      if (validReadings.length === 0) {
        notification.warning({
          description: 'No valid readings to fetch simulations.',
          message: undefined,
        });
        return;
      }
      const pastForecastresult =
        await forecastService.postParameterPastForecast(
          validReadings[0].originalParameterId,
          null,
          null,
          simulationId,
        );
      setPastForecastData(pastForecastresult);
    } catch (error) {
      console.error('Error fetching forecast data:', error);
    }
  };

  const fetchSimulations = useCallback(() => {
    const seen = new Set<string>();

    const validReadings = readingsList.filter((item) => {
      if (!item) return false;

      const key = JSON.stringify([
        item.originalParameterId,
        item.originalSourceSerialNumber,
      ]);
      if (seen.has(key)) return false;
      seen.add(key);

      return (
        item.originalSourceSerialNumber == null &&
        selectedSensors.some(
          (sensor) =>
            sensor.serialNumberTool === item.serialNumberMeasurementTool,
        )
      );
    });

    if (validReadings.length === 0) {
      notification.warning({
        description: 'No valid readings to fetch simulations.',
        message: undefined,
      });
      return;
    }

    const security = JSON.parse(localStorage.getItem('security') || '{}');
    const organizationId = security.organizationId;

    const [start, end] = selectedDateRange;

    setIsLoadingSimulations(true);
    setHasSimulationError(false);

    getSimulationHistory(organizationId, {
      page: 1,
      size: 300,
      referenceId: validReadings[0].originalParameterId,
      simulationType: 'F',
      startdate: dayjs(start).format('YYYY-MM-DD HH:mm:ss'),
      enddate: dayjs(end).format('YYYY-MM-DD HH:mm:ss'),
      status: 'COMPLETED',
    })
      .then((response) => {
        const sims = [...response.simulationsResponseDTOList]?.sort((a, b) => {
          if (a.strategyName < b.strategyName) return -1;
          if (a.strategyName > b.strategyName) return 1;
          return dayjs(a.startedAt).diff(dayjs(b.startedAt));
        }) || [];
        setSimulationsList(sims);
        setHasSimulationError(false);

        if (sims.length === 0) {
          notification.warning({
            description: 'This parameter is not used by any simulation.',
            message: undefined,
          });
          setSelectedSimulation(undefined);
        } else {
          setSelectedSimulation(sims[0].id);
          forecastHandler(sims[0].id);
        }
      })
      .catch((error) => {
        console.error('Error fetching simulations', error);
        notification.error({
          description: 'Failed to fetch forecast simulations.',
          message: undefined,
        });
        setSelectedSimulation(undefined);
        setSimulationsList([]);
        setHasSimulationError(true);
      })
      .finally(() => {
        setIsLoadingSimulations(false);
      });
  }, [readingsList, selectedSensors, selectedDateRange, forecastHandler]);

  useEffect(() => {
    if (selectedSimulation) {
      fetchPastForecastData(selectedSimulation);
    }
  }, [selectedSimulation]);

  useEffect(() => {
    if (readingsList && readingsList.length > 0) {
      const seen = new Set();
      const data = readingsList.filter((item) => {
        if (item) {
          const tuple = JSON.stringify([
            item.originalParameterId,
            item.originalSourceSerialNumber,
          ]);
          if (seen.has(tuple)) {
            return false;
          }
          seen.add(tuple);
        }
        return (
          item?.originalSourceSerialNumber == null &&
          context.selectedSensors.find(
            (sensor) =>
              sensor.serialNumberTool == item.serialNumberMeasurementTool,
          )
        );
      });

      if (data.length != 1) {
        setPastForecastData([]);
        setShowPastForecast(false);
      }
    } else {
      setPastForecastData([]);
      setShowPastForecast(false);
    }
  }, [readingsList, showPastForecast]);

  return {
    simulationsList,
    selectedSimulation,
    fetchSimulations,
    setSelectedSimulation,
    pastForecastData,
    setPastForecastData,
    setShowPastForecast,
    showPastForecast,
    isLoadingSimulations,
    hasSimulationError,
  };
}
